import { useState } from 'react'
import { clsx } from 'clsx'
import type { Therapist } from '../../stores/chatStore'

interface TherapistAvatarProps {
  therapist?: Therapist | null
  size?: 'sm' | 'md' | 'lg' | 'xl'
  showStatus?: boolean
  isOnline?: boolean
  className?: string
}

const sizeClasses = {
  sm: 'w-8 h-8 text-sm',
  md: 'w-11 h-11 text-base',
  lg: 'w-14 h-14 text-lg',
  xl: 'w-20 h-20 text-2xl',
}

const statusSizeClasses = {
  sm: 'w-2 h-2',
  md: 'w-2.5 h-2.5',
  lg: 'w-3 h-3',
  xl: 'w-4 h-4',
}

const therapistColors: Record<string, { bg: string; text: string; ring: string }> = {
  wendy: {
    bg: 'bg-gradient-to-br from-wendy to-wendy-dark',
    text: 'text-white',
    ring: 'ring-wendy',
  },
  artem: {
    bg: 'bg-gradient-to-br from-artem to-artem-dark',
    text: 'text-white',
    ring: 'ring-artem',
  },
  finch: {
    bg: 'bg-gradient-to-br from-finch to-finch-dark',
    text: 'text-white',
    ring: 'ring-finch',
  },
  harold: {
    bg: 'bg-gradient-to-br from-finch to-finch-dark',
    text: 'text-white',
    ring: 'ring-finch',
  },
  una: {
    bg: 'bg-gradient-to-br from-una to-una-dark',
    text: 'text-white',
    ring: 'ring-una',
  },
}

export default function TherapistAvatar({
  therapist,
  size = 'md',
  showStatus = false,
  isOnline = false,
  className,
}: TherapistAvatarProps) {
  const [imgFailed, setImgFailed] = useState(false)
  const type = therapist?.type || 'wendy'
  const colors = therapistColors[type] || therapistColors.wendy
  const initial = (therapist?.name || 'T')[0].toUpperCase()

  return (
    <div className={clsx('relative inline-block', className)}>
      <div
        className={clsx(
          'avatar',
          sizeClasses[size],
          colors.bg,
          colors.text,
          colors.ring,
          'ring-2 shadow-soft'
        )}
      >
        {therapist?.avatarUrl && !imgFailed ? (
          <img
            src={therapist.avatarUrl}
            alt={therapist.name}
            className="w-full h-full object-cover"
            onError={() => setImgFailed(true)}
          />
        ) : (
          <span className="font-semibold">
            {initial}
          </span>
        )}
      </div>

      {/* Online status indicator */}
      {showStatus && (
        <span
          className={clsx(
            'absolute bottom-0 right-0 rounded-full border-2 border-white',
            statusSizeClasses[size],
            isOnline ? 'bg-success' : 'bg-neutral-400'
          )}
        />
      )}
    </div>
  )
}
